import { ROUTES } from '@/app/routes';
import Pressable from '@/components/motion/pressable';
import { CardDescription, CardTitle } from '@/components/ui/card';
import { $features } from '@/entities/features-entity';
import { cn } from '@/lib/utils';
import { useUnit } from 'effector-react';
import { ChevronRightIcon, ContainerIcon, GlobeIcon, HistoryIcon, InfoIcon } from 'lucide-react';
import { useNavigate } from 'react-router';

type ChartColor = 'chart-1' | 'chart-2' | 'primary';

interface HubItem {
	label: string;
	description: string;
	path: string;
	icon: typeof GlobeIcon;
	color: ChartColor;
	disabled?: boolean;
}

const COLOR_CLASSES: Record<ChartColor, { iconBg: string; title: string }> = {
	'chart-1': { iconBg: 'bg-chart-1/15', title: 'text-chart-1' },
	'chart-2': { iconBg: 'bg-chart-2/15', title: 'text-chart-2' },
	primary: { iconBg: 'bg-primary/10', title: 'text-primary' },
};

export default function ApkHubSection() {
	const navigate = useNavigate();
	const features = useUnit($features);

	const hubItems: HubItem[] = [
		{
			label: 'Билдер',
			description: 'Создание APK с вашими настройками',
			path: ROUTES.apkBuilder,
			icon: ContainerIcon,
			color: 'primary',
			disabled: !features.builderEnabled,
		},
		{
			label: 'Домены',
			description: 'Подключение доменов для APK',
			path: ROUTES.apkDomains,
			icon: GlobeIcon,
			color: 'chart-1',
			disabled: !features.domainsEnabled,
		},
		{
			label: 'Билды',
			description: 'История сборок и скачивание APK',
			path: ROUTES.apkBuilds,
			icon: HistoryIcon,
			color: 'chart-2',
		},
	];

	function go(item: HubItem) {
		if (item.disabled) return;
		navigate(item.path);
	}

	return (
		<div className='flex flex-col gap-3'>
			{!features.builderEnabled ? (
				<div className='flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground'>
					<InfoIcon className='mt-0.5 size-4 shrink-0' />
					<span>Билдер временно отключён администратором. Создание новых сборок недоступно.</span>
				</div>
			) : null}

			{!features.domainsEnabled ? (
				<div className='flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground'>
					<InfoIcon className='mt-0.5 size-4 shrink-0' />
					<span>Добавление доменов временно отключено администратором.</span>
				</div>
			) : null}

			<div className='flex flex-col gap-2'>
				{hubItems.map(item => {
					const Icon = item.icon;
					const colors = COLOR_CLASSES[item.color];
					return (
						<Pressable
							key={item.path}
							role='button'
							tabIndex={item.disabled ? -1 : 0}
							onClick={() => go(item)}
							className={cn(
								'rounded-xl border border-border bg-card shadow-sm overflow-hidden text-left',
								item.disabled ? 'opacity-50 cursor-not-allowed' : 'active:bg-accent/30 cursor-pointer',
							)}
						>
							<div className='flex items-center justify-between gap-3 px-3 py-3'>
								<div className='flex items-center gap-3 min-w-0'>
									<div className={cn('rounded-lg p-2 shrink-0', colors.iconBg)}>
										<Icon className={colors.title} size={20} />
									</div>
									<div className='min-w-0'>
										<CardTitle className={cn('text-sm', colors.title)}>{item.label}</CardTitle>
										<CardDescription className='truncate'>{item.description}</CardDescription>
									</div>
								</div>
								<ChevronRightIcon size={18} className='text-muted-foreground shrink-0' />
							</div>
						</Pressable>
					);
				})}
			</div>

			<p className='text-xs text-muted-foreground px-0.5'>
				Домены APK — отдельный контур от лендингов. Управление доменами, сборкой и историей билдов.
			</p>
		</div>
	);
}
